import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Upload,
  Alert,
  Image,
  Popconfirm,
  Divider,
  Space,
  Row,
  Switch,
  Radio,
  Card,
  Flex,
} from "antd";

import { countryCodeOptions } from "@shared/lib/constants";
import {
  GenderEnum,
  ICreateInfluencerRequest,
  IInfluencer,
  INFLUENCER_ACCOUNT_STATUS,
  InfluencerContact,
  InfluencerType,
} from "@shared/lib/types/influencer";
import styles from "../styles.module.scss";

import { CircularProgress } from "@shared/ui";
import { useGetAgencies } from "@entities/agency";
import { ACCOUNT_STATUS } from "@shared/lib/types";
import { ToastService } from "@shared/lib/services";
import { useGetAgency } from "@entities/agency/lib/hooks/useGetAgency";

const { Option } = Select;
interface Props {
  onSubmit: (values: Partial<InfluencerContact>) => void;
  linkedAgencies?: IInfluencer["linkedAgencies"];
  isSubmitting: boolean;
  initialValues?: InfluencerContact;
}

export const ContactForm = ({
  onSubmit,
  isSubmitting,
  initialValues,
  linkedAgencies = [],
}: Props) => {
  const [form] = Form.useForm();

  const [selectedAgency, setselectedAgency] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
  >({
    label: initialValues?.agency?.name || "",
    value: initialValues?.agency?._id || "",
  });

  const handleSelectAgency = (value: string) => {
    const selected = linkedAgencies.find((item) => item._id === value);
    if (selected) {
      setselectedAgency({
        label: selected.name,
        value: selected._id,
      });
      form.setFieldValue("agencyId", value);
      form.setFieldValue("agencyUserId", undefined);
    }
  };

  const type = Form.useWatch("type", form);
  const agencyId = Form.useWatch("agencyId", form);
  const agencyUserId = Form.useWatch("agencyUserId", form);

  const { agency, isFetching } = useGetAgency(agencyId);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...initialValues,
        agencyId: initialValues?.agency?._id,
        isPrimaryContact: initialValues?.isPrimaryContact || false,
        countryCode: initialValues?.countryCode || "+91",
        type: initialValues?.type || InfluencerType.self,
      }}
      onFinish={(values) => {
        const { agencyId, ...rest } = values;
        onSubmit({
          ...rest,
          phoneNumber: values.phoneNumber
            ? values.phoneNumber.replace(/\s/g, "")
            : undefined,
          agency: agencyId ? agencyId : undefined,
          emailId: values.emailId
            ? values.emailId.replace(/\s/g, "")
            : undefined,
        });
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div className={styles.form}>
        <Form.Item name="type" label="Contact Type">
          <Radio.Group
            style={{ display: "flex", gap: 10, flexDirection: "column" }}
            onChange={(e) => {
              const value = e.target.value;
              if (value !== InfluencerType.agency) {
                form.setFieldsValue({
                  agencyId: undefined,
                  agencyUserId: undefined,
                });
              }
            }}
          >
            <Radio value={InfluencerType.self}>Influencer’s Contact</Radio>
            <Radio value={InfluencerType.manager}>Manager’s Contact</Radio>
            <Radio value={InfluencerType.agency}>Agency’s Contact</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Influencer's Contact Fields */}
        {(type === InfluencerType.self || !type) && (
          <>
            <Form.Item
              name="emailId"
              label="Email Id (Influencer’s email ID)"
              rules={[
                { pattern: /^\S+@\S+\.\S+$/, message: "Invalid email format" },
              ]}
            >
              <Input placeholder="Enter Influencer's Email ID" />
            </Form.Item>
            <Form.Item label="Phone Number (Influencer’s phone number)">
              <Input.Group compact>
                <Form.Item
                  name="countryCode"
                  noStyle
                  rules={[
                    { required: false, message: "Country code is required" },
                  ]}
                >
                  <Select style={{ width: "30%" }}>
                    {countryCodeOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="phoneNumber"
                  noStyle
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: "Phone number must be numeric",
                    },
                  ]}
                >
                  <Input
                    style={{ width: "70%" }}
                    placeholder="Enter Influencer's Phone Number"
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </>
        )}

        {/* Manager's Contact Fields */}
        {type === InfluencerType.manager && (
          <>
            <Form.Item
              name="name"
              label="Full Name (Manager’s full name)"
              rules={[
                { required: true, message: "Manager's name is required" },
              ]}
            >
              <Input placeholder="Enter Manager's Full Name" />
            </Form.Item>
            <Form.Item
              name="emailId"
              label="Email Id (Manager’s Email ID)"
              rules={[
                { pattern: /^\S+@\S+\.\S+$/, message: "Invalid email format" },
              ]}
            >
              <Input placeholder="Enter Manager's Email ID" />
            </Form.Item>
            <Form.Item label="Phone Number (Manager)">
              <Input.Group compact>
                <Form.Item
                  name="countryCode"
                  noStyle
                  rules={[
                    { required: false, message: "Country code is required" },
                  ]}
                >
                  <Select style={{ width: "30%" }}>
                    {countryCodeOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="phoneNumber"
                  noStyle
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: "Phone number must be numeric",
                    },
                  ]}
                >
                  <Input
                    style={{ width: "70%" }}
                    placeholder="Enter Manager's Phone Number"
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </>
        )}

        {/* Agency's Contact Fields */}
        {type === InfluencerType.agency && (
          <>
            <Form.Item
              label="Select an Agency"
              name="agencyId"
              rules={[
                {
                  required: true,
                  message: "Agency is required for Agency type",
                },
              ]}
            >
              <Select
                showSearch
                value={selectedAgency?.value || undefined}
                onChange={handleSelectAgency}
                filterOption={(input, option) => true}
              >
                {selectedAgency && (
                  <Select.Option
                    key={selectedAgency.value}
                    value={selectedAgency.value}
                  >
                    {selectedAgency.label}
                  </Select.Option>
                )}
                {linkedAgencies.length > 0 ? (
                  linkedAgencies
                    .filter((item) => item._id !== selectedAgency?.value)
                    .map((location) => (
                      <Select.Option key={location._id} value={location._id}>
                        {location.name}
                      </Select.Option>
                    ))
                ) : (
                  <Select.Option disabled key="no-data" value="no-data">
                    No data available
                  </Select.Option>
                )}
              </Select>
            </Form.Item>
            {agencyId && (
              <Form.Item
                name="agencyUserId"
                label="Select Employee"
                rules={[
                  {
                    required: true,
                    message: "Select Employee",
                  },
                ]}
              >
                {isFetching ? (
                  <Row justify="center">
                    <CircularProgress />
                  </Row>
                ) : (
                  <Card>
                    <Radio.Group
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                      value={agencyUserId}
                      onChange={(e) =>
                        form.setFieldValue("agencyUserId", e.target.value)
                      }
                    >
                      {" "}
                      {agency?.agencyUsers?.map((contact) => (
                        <div>
                          <Radio value={contact.agencyUserId}>
                            <Flex
                              justify="space-between"
                              style={{ width: "400px" }}
                            >
                              <Flex vertical gap={2} style={{ width: "50%" }}>
                                <div className={styles.contactName}>
                                  {contact.name}
                                </div>
                                {contact?.phoneNumber && (
                                  <div className={styles.contactPhone}>
                                    {contact?.countryCode}-
                                    {contact?.phoneNumber}
                                  </div>
                                )}
                              </Flex>
                              <Flex
                                vertical
                                gap={2}
                                style={{ width: "50%", textAlign: "right" }}
                              >
                                <div className={styles.contactRole}>
                                  {contact.role}
                                </div>
                                {contact?.emailId && (
                                  <div className={styles.contactEmail}>
                                    {contact?.emailId}
                                  </div>
                                )}
                              </Flex>
                            </Flex>
                          </Radio>
                          <Divider style={{ margin: 0, marginTop: 5 }} />
                        </div>
                      ))}
                    </Radio.Group>
                  </Card>
                )}
              </Form.Item>
            )}
          </>
        )}

        <Form.Item
          label="Is this the primary contact of Creator?"
          name="isPrimaryContact"
        >
          <Switch />
        </Form.Item>
      </div>
      <div className={styles.footer}>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? "Saving" : "Save"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default ContactForm;
